import type { NativeBridgeGlobal } from './bridge';

/**
 * Адаптер моста для запуска web-сервиса в iframe веб-оболочки (вместо нативного WebView).
 *
 * В нативке `window.__nativeBridge` инжектит хост (injectedJavaScriptBeforeContentLoaded).
 * В браузере хост-оболочка НЕ может вписать объект в cross-origin iframe (SOP, а postMessage
 * не передаёт функции). Поэтому тот же `__nativeBridge` создаёт сам сервис — этим адаптером,
 * с идентичным интерфейсом (receive/subscribe/send). Сервисный код (BridgeSdk) не меняется,
 * меняется только транспорт: send → parent.postMessage, входящие message → receive.
 */
export interface InstallIframeBridgeOptions {
  /**
   * Разрешённые origin родителя (оболочки). Если заданы — принимаем сообщения и шлём ответы
   * только этим origin. Если не заданы — origin фиксируется по первому полученному сообщению.
   */
  allowedOrigins?: string[];
  /** Явный целевой origin для исходящих (приоритетнее referrer и allowedOrigins). */
  targetOrigin?: string;
}

/** `true`, если код исполняется внутри iframe (в том числе cross-origin). */
export function isInsideIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Доступ к window.top из cross-origin iframe бросает — значит мы в iframe.
    return true;
  }
}

function referrerOrigin(): string {
  try {
    return document.referrer ? new URL(document.referrer).origin : '';
  } catch {
    return '';
  }
}

/**
 * Ставит `window.__nativeBridge` поверх postMessage. Идемпотентно: при повторном вызове
 * возвращает уже установленный объект (можно вызвать с опциями ДО bridge.init()).
 */
export function installIframeBridge(
  options: InstallIframeBridgeOptions = {}
): NativeBridgeGlobal {
  if (window.__nativeBridge) return window.__nativeBridge;

  const allowed = options.allowedOrigins;
  let listeners: Array<(msgStr: string) => void> = [];
  let queue: string[] = [];
  let subscribed = false;

  // Origin оболочки фиксируем по первому валидному сообщению; до того шлём на initialTarget.
  let lockedOrigin = '';
  const initialTarget =
    options.targetOrigin || (allowed && allowed[0]) || referrerOrigin() || '';

  const isOriginAllowed = (origin: string): boolean => {
    if (allowed && allowed.length > 0) return allowed.includes(origin);
    if (lockedOrigin) return origin === lockedOrigin;
    return true; // первый контакт — принимаем и фиксируем origin ниже
  };

  const bridge: NativeBridgeGlobal = {
    receive(msgStr: string) {
      if (!subscribed) {
        queue.push(msgStr);
        return;
      }
      for (const fn of listeners) {
        try {
          fn(msgStr);
        } catch {
          // изоляция подписчиков
        }
      }
    },
    subscribe(fn) {
      listeners.push(fn);
      subscribed = true;
      if (queue.length) {
        const pending = queue;
        queue = [];
        for (const msg of pending) fn(msg);
      }
      return () => {
        listeners = listeners.filter((l) => l !== fn);
      };
    },
    send(msgStr: string) {
      const target = lockedOrigin || initialTarget || '*';
      window.parent.postMessage(msgStr, target);
    }
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    if (!isOriginAllowed(event.origin)) return;
    if (!lockedOrigin) lockedOrigin = event.origin; // фиксируем origin оболочки
    const data =
      typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
    bridge.receive(data);
  };

  window.addEventListener('message', onMessage);
  window.__nativeBridge = bridge;
  return bridge;
}
